import PropTypes from 'prop-types';
import React from 'react';
import cx from 'classnames';
import { ThemeProvider } from 'react-css-themr';
import styles from './documentUploadStyles.scss';
import { FontIcon, Input } from 'react-toolbox';
import FlexBox from 'flexbox-react';
import formStyles from 'styles/form';
import RTInput from 'styles/input';
import RTDropdown from 'styles/dropdown';
import { filter } from 'lodash';
import StyledSelect from 'components/StyledSelect';
import ColoredButton from 'components/ColoredButton';
import { isImage } from '../../../utils';
import {
  DOCUMENT_TYPES,
  VALID_DOC_FILE_UPLOADS,
  MAX_FILE_SIZE,
  INSTALLER_DOCUMENT_TYPES,
  IFI_SUB_DOCUMENT_TYPES,
} from 'config/constants';
import Loading from 'components/Loading';
import MessageBox from 'components/MessageBox';

const theme = { RTInput, RTDropdown };
const largeInput = cx(formStyles.inputs, formStyles.largeInput);
const cancelClass = cx([styles.modalButton, formStyles.cancelButton, formStyles.actionButton]);
const uploadClass = cx([styles.modalButton, formStyles.actionButton]);
const errorTextClass = cx([styles.errorText, styles.text]);
const BYTES_PER_MB = 1000000;
const DOCUMENT_OPTIONS = DOCUMENT_TYPES.sort().map(DocumentType => ({
  value: DocumentType, label: DocumentType,
}));

class DocumentsUpload extends React.Component {
  constructor(props) {
    super(props);
    const filesTotalBytes = this.getFilesTotalBytes(this.props.files);
    this.state = {
      theFiles: this.props.files.map((file) => {
        const adjustedFile = file;
        adjustedFile.extension = file.name.split('.').pop();
        adjustedFile.sizeInMb = (file.size / BYTES_PER_MB).toFixed(3);
        adjustedFile.tooLarge = file.size > MAX_FILE_SIZE;
        return adjustedFile;
      }),
      errors: this.props.files.map(() => ({})),
      filesTotalBytes: this.getFilesTotalBytes(this.props.files),
      filesTotalMB: (filesTotalBytes / BYTES_PER_MB).toFixed(3),
    };
    this.handleSubmit = this.handleSubmit.bind(this);
  }

  getFilesTotalBytes = files => files.reduce((total, file) => total + file.size, 0);

  handleSubmit = () => {
    const { theFiles } = this.state;
    const errors = this.handleErrors();
    if (errors) return;
    const formData = new FormData();
    theFiles.map((file) => {
      const documentName = file.document_name || file.name.replace(/\.[^/.]+$/, '');
      formData.append('document_upload[assets][][asset]', file);
      formData.append('document_upload[assets][][document_name]', documentName);
      formData.append('document_upload[assets][][document_type]', file.document_type);
      return null;
    });
    this.props.onSubmit(formData);
  }

  handleErrors = () => {
    const { theFiles, errors } = this.state;
    let shouldReturn = false;
    const errorText = "This field can't be blank";
    theFiles.map((file, i) => {
      errors[i] = {};
      if (!file.document_type) {
        errors[i].document_type = errorText;
        shouldReturn = true;
      }
      const fileExtension = file.name.split('.').pop();
      if (VALID_DOC_FILE_UPLOADS.indexOf(fileExtension.toLowerCase()) === -1) {
        if (errors[i].fileValidation && errors[i].fileValidation.length) {
          errors[i].fileValidation += ', ';
        } else {
          errors[i].fileValidation = '';
        }
        errors[i].fileValidation += 'File extension is not currently supported. Please select another file.';
        shouldReturn = true;
      }
      return null;
    });
    this.setState({ errors });
    return shouldReturn;
  }

  deleteFile = (index) => {
    const { theFiles, errors } = this.state;
    theFiles.splice(index, 1);
    errors.splice(index, 1);
    if (theFiles.length === 0) {
      this.props.onCancel();
    } else {
      const filesTotalBytes = this.getFilesTotalBytes(theFiles);
      const filesTotalMB = (filesTotalBytes / BYTES_PER_MB).toFixed(3);
      this.setState({ theFiles, errors, filesTotalBytes, filesTotalMB });
    }
  }

  handleChange = (key, value, index) => {
    const files = this.state.theFiles;
    const file = files[index];
    file[key] = value;
    this.setState({ theFiles: files });
  }

  renderErrors = () => {
    const globalError = this.props.uploadError && this.props.uploadError.document_uploads;
    if (this.props.uploadError && this.props.uploadError.document_uploads) {
      return (<div className={styles.errorContainer}>
        <MessageBox error message={globalError.join(', ')} />
      </div>);
    }
    return null;
  }

  render = () => {
    const {
      onCancel, uploadingDocuments, role, cv, source, position, isDoingMeasure,
    } = this.props;
    const { theFiles, errors, filesTotalBytes, filesTotalMB } = this.state;
    const theUploadErrors = this.renderErrors();
    const singleFileTooLarge = theFiles.findIndex(file => file.tooLarge) > -1;
    const exceedsMaxUploadSize = filesTotalBytes > MAX_FILE_SIZE;
    const hasSizeErrors = singleFileTooLarge || exceedsMaxUploadSize;
    const isIfi = (source || '').toUpperCase() === 'IFI';
    const isSub = ['installer', 'crew'].includes(role);
    let docOptions = DOCUMENT_OPTIONS;
    if (cv) {
      docOptions = filter(docOptions, o => ['Floor Plan'].includes(o.value));
    } else if (isIfi && isSub) {
      const allowedTypes = isDoingMeasure
        ? [...IFI_SUB_DOCUMENT_TYPES, 'Assessment/Refigure']
        : IFI_SUB_DOCUMENT_TYPES;
      docOptions = filter(docOptions, o => allowedTypes.includes(o.value));
    } else if (isSub) {
      docOptions = filter(docOptions, o => INSTALLER_DOCUMENT_TYPES.includes(o.value));
    } else if (isIfi && position === 'Installation Coordinator') {
      docOptions = filter(docOptions, o => IFI_SUB_DOCUMENT_TYPES.includes(o.value));
    }
    return (<ThemeProvider theme={theme}>
      <div className={styles.back}>
        <div className={styles.container}>
          <p className={styles.title}>Document Upload</p>
          <p className={styles.text}>Total Upload Size: {filesTotalMB}MB.</p>
          {exceedsMaxUploadSize && <p className={`${styles.text} ${styles.warningText}`}>
            Your upload exceeds maximum size of {MAX_FILE_SIZE / BYTES_PER_MB}GB.
          </p>}
          {uploadingDocuments && <div className={styles.loadingNotificationContainer}>
            <div className={styles.loadingSpinner}>
              <Loading />
            </div>
            <p className={styles.text}>
              Upload has started. Large documents may take up to a minute.
            </p>
          </div>}
          <div className={styles.errorMessage}>
            {theUploadErrors}
          </div>
          <div className={styles.filesContainer}>
            {theFiles.map((file, i) => (<div
              className={styles.fileContainer}
              key={`${file.name}${file.lastModified}`}
            >
              {isImage(file.type) ? <img
                className={cx([styles.preview, styles.center])}
                src={file.preview}
                alt={file.name}
              /> : <FontIcon
                className={styles.center}
                value="insert_drive_file"
              />}
              <span className={styles.text}>{file.name}</span>
              <span> {file.sizeInMb}MB</span>
              {file.tooLarge && <span className={errorTextClass}>
                exceeds the maximum file size limit of 20MB
              </span>}
              <button
                className={styles.deleteButton}
                onClick={() => this.deleteFile(i)}
              >
                <FontIcon className={styles.deleteIcon} value="delete" />
              </button>
              <FlexBox className={styles.inputs}>
                <StyledSelect
                  className={largeInput}
                  label="Document type"
                  onChange={value => this.handleChange('document_type', value, i)}
                  options={docOptions}
                  value={file.document_type}
                  placeholder={'Select Document Type'}
                  error={errors[i].document_type || ''}
                  required
                />
                <Input
                  label="File Name"
                  className={largeInput}
                  value={file.document_name}
                  onChange={value => this.handleChange('document_name', value, i)}
                  error={errors[i].document_name || ''}
                  placeholder={file.name.replace(/\.[^/.]+$/, '')}
                  required
                />
              </FlexBox>
              <span className={errorTextClass}>{errors[i].fileValidation}</span>
            </div>))}
          </div>
          <div className={styles.actionButtons}>
            <ColoredButton
              className={uploadClass}
              color="blue"
              disabled={hasSizeErrors}
              label="Upload"
              onClick={() => this.handleSubmit()}
            />
            <ColoredButton
              className={cancelClass}
              color="gray"
              label="Cancel"
              onClick={onCancel}
            />
          </div>
          <div className={uploadingDocuments && styles.loadingOverlay} />
        </div>
      </div>
    </ThemeProvider>);
  }
}

DocumentsUpload.propTypes = {
  onCancel: PropTypes.func,
  onSubmit: PropTypes.func,
  files: PropTypes.array,
  uploadError: PropTypes.string,
  uploadingDocuments: PropTypes.bool,
  role: PropTypes.string,
  cv: PropTypes.bool,
  source: PropTypes.string,
  position: PropTypes.string,
  isDoingMeasure: PropTypes.bool,
};

export default DocumentsUpload;



// WEBPACK FOOTER //
// ./client/app/shared/components/DocumentsUpload/index.jsx